import "./TableNoteProductsModal.scss";
import { Modal } from "react-bootstrap";
import { useState, useEffect, useCallback, useRef } from "react";
import getTableNotesOrders from "../../../utility/getTableNotesOrders";
import getIcon from "../../../utility/getIcon";
import { MinusIcon, PlusIcon } from "../../../components/Icon";
import GradientButton from "../../../components/buttons/gradientButton/GradientButton";
import { updateData } from "../../../utility/updateData";
import { useTranslation } from "react-i18next";
import { formatOrderDateTime, toOrderTimestamp } from "../../../utility/orderDateTime";

const getProductTotal = (product) => {
  const totalDue = Number(product?.totalDue);
  if (Number.isFinite(totalDue)) return totalDue;

  const price = Number(product?.price);
  const quantity = Number(product?.quantity);
  return (Number.isFinite(price) ? price : 0) * (Number.isFinite(quantity) ? quantity : 0);
};

function TableNoteProductsModal({ isOpen, onClose, table }) {
  const [tableNoteItems, setTableNoteItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const { t } = useTranslation("tablePlan");
  const isMountedRef = useRef(true);

  const fetchTableNoteItems = useCallback(async () => {
    if (!table?.id || !table?.orgId) {
      console.error("Required properties of table are missing");
      return null;
    }

    try {
      const orders = await getTableNotesOrders({
        tableId: table.id,
        orgId: table.orgId,
      });

      if (!isMountedRef.current) return null;

      const items = [];
      let total = 0;

      [...orders]
        .sort((firstOrder, secondOrder) => {
          const firstTimestamp = toOrderTimestamp(firstOrder?.createdAt) ?? 0;
          const secondTimestamp = toOrderTimestamp(secondOrder?.createdAt) ?? 0;
          return secondTimestamp - firstTimestamp;
        })
        .forEach((order) => {
          if (Array.isArray(order?.products)) {
            order.products.forEach((product, productIndex) => {
              if (product) {
                const totalDue = getProductTotal(product);
                items.push({
                  ...product,
                  totalDue,
                  quantity: product.quantity ?? 0,
                  sourceOrderId: order.id,
                  sourceProductIndex: productIndex,
                  orderedAt: order.createdAt,
                });

                total += totalDue;
              }
            });
          }
        });

      if (!isMountedRef.current) return null;

      setTotal(total);
      setOrders(orders);
      setTableNoteItems(items);

      return orders;
    } catch (error) {
      console.error("Error getting the table note's orders: ", error);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [table?.id, table?.orgId]);

  useEffect(() => {
    if (!table || !isOpen) return;

    isMountedRef.current = true;
    setLoading(true);
    fetchTableNoteItems();

    return () => {
      isMountedRef.current = false;
    };
  }, [isOpen, table, fetchTableNoteItems]);

  async function updateItemQuantity(item, isAdd) {
    const orderToUpdate = orders.find((order) => order.id === item.sourceOrderId);
    if (!orderToUpdate) return;

    let updatedProducts;
    if (Array.isArray(orderToUpdate.products) && orderToUpdate.products.length > 0) {
      updatedProducts = orderToUpdate.products
        .map((product, productIndex) => {
          if (productIndex === item.sourceProductIndex) {
            const price = Number(product.price) || 0;
            const currentTotal = getProductTotal(product);
            const quantity = Number(product.quantity) || 0;
            if (isAdd) {
              return {
                ...product,
                quantity: quantity + 1,
                totalDue: currentTotal + price,
              };
            } else if (quantity > 1) {
              return {
                ...product,
                quantity: quantity - 1,
                totalDue: currentTotal - price,
              };
            }
            return null;
          }
          return product;
        })
        .filter(Boolean);
    }

    const updatedOrder = {
      ...orderToUpdate,
      products: updatedProducts,
      totalAmount: updatedProducts.reduce((sum, product) => sum + getProductTotal(product), 0),
    };

    await updateData("Orders", updatedOrder.id, updatedOrder);

    const fetchedOrders = await fetchTableNoteItems();

    if (Array.isArray(fetchedOrders) && fetchedOrders.length > 0) {
      const newTableTotal = fetchedOrders.reduce(
        (sum, order) => sum + (order?.totalAmount ?? 0),
        0,
      );
      await updateData("Tables", table.id, { amountToBePaid: newTableTotal });
    }
  }

  return (
    <Modal show={isOpen} onHide={onClose} centered size="lg" className="table-note-modal">
      <Modal.Header>
        <Modal.Title>{t("table_note_title")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <p className="table-note-modal-loader">{t("table_note_loading")}</p>
        ) : tableNoteItems && tableNoteItems.length > 0 ? (
          tableNoteItems.map((item) => (
            <ProductItem
              key={`${item.sourceOrderId}-${item.sourceProductIndex}`}
              item={item}
              updateItemQuantity={updateItemQuantity}
              t={t}
            />
          ))
        ) : (
          <p className="table-note-modal-empty">{t("table_note_empty")}</p>
        )}
      </Modal.Body>
      <Modal.Footer>
        {orders.some((order) => order.note) && (
          <div className="table-note-comments">
            <p className="table-note-comments-title">{t("table_note_comments")}</p>
            <ul className="table-note-comments-list">
              {orders
                .filter((order) => order.note)
                .map((order) => (
                  <li key={order.id} className="table-note-comment-item">
                    {order.note}
                  </li>
                ))}
            </ul>
          </div>
        )}
        <p className="total-amount">
          <span className="total-amount-title">{t("table_note_total")} : </span>
          {total}€
        </p>
        <GradientButton onPress={onClose}>{t("table_note_close")}</GradientButton>
      </Modal.Footer>
    </Modal>
  );
}

const ProductItem = ({ item, updateItemQuantity, t }) => {
  const orderedAt = formatOrderDateTime(item.orderedAt);

  return (
    <div className="product-item">
      <div className="product-item-info">
        <div className="product-item-info-image-container">
          <img src={getIcon(item.category, item.customIcon)} alt="item" />
        </div>
        <div className="product-item-info-text">
          <h3 className="product-item-info-text-title">{item.title}</h3>
          <div className="product-item-info-text-price">
            <span>x{item.quantity}</span>
            <span className="product-item-info-text-price-amount">{item.totalDue.toFixed(2)}€</span>
          </div>
          {orderedAt && (
            <div className="product-item-info-text-date">
              {t("table_note_ordered_at", { date: orderedAt })}
            </div>
          )}
        </div>
      </div>
      <div className="product-item-button-group">
        <button onClick={() => updateItemQuantity(item, true)}>
          <PlusIcon />
        </button>
        <div className="product-item-button-group-divider"></div>
        <button onClick={() => updateItemQuantity(item, false)}>
          <MinusIcon />
        </button>
      </div>
    </div>
  );
};

export default TableNoteProductsModal;
